<template>
  <div class="pa-4">
    <v-row dense class="mb-4 align-end">
      <v-col cols="12" md="6">
        <v-select
          v-model="selectedCode"
          :items="categorySelectItems"
          item-title="label"
          item-value="value"
          :label="$t('leko_employees.car_part_groups.grafiche_pick_category')"
          variant="outlined"
          hide-details
          density="comfortable"
          color="red-darken-3"
          :loading="reloadLoading || (apiMode && partsLoading)"
        />
      </v-col>
      <v-col cols="12" md="auto">
        <v-btn
          v-if="!apiMode"
          color="red-darken-3"
          variant="flat"
          :disabled="!selectedCode"
          @click="persist"
        >
          <v-icon start> mdi-content-save </v-icon>
          {{ $t("leko_employees.car_part_groups.grafiche_save_lines") }}
        </v-btn>
      </v-col>
      <v-col cols="12" md="auto">
        <v-btn
          v-if="!apiMode"
          color="grey-darken-3"
          variant="outlined"
          :disabled="!selectedCode"
          @click="openLineEditor(null)"
        >
          <v-icon start> mdi-plus </v-icon>
          {{ $t("leko_employees.car_part_groups.grafiche_add_title") }}
        </v-btn>
      </v-col>
      <v-col cols="12" md="auto">
        <v-btn
          color="grey-darken-2"
          variant="text"
          :loading="reloadLoading"
          @click="reload"
        >
          <v-icon start> mdi-refresh </v-icon>
          {{ $t("leko_employees.car_part_groups.grafiche_reload_from_server") }}
        </v-btn>
      </v-col>
      <v-col cols="12">
        <v-alert v-if="apiMode" type="info" density="compact" variant="tonal">
          {{ $t("leko_employees.car_part_groups.grafiche_api_titles_hint") }}
        </v-alert>
      </v-col>
      <v-col cols="12">
        <v-alert
          v-if="banner"
          :type="banner.type"
          density="compact"
          closable
          @click:close="banner = null"
        >
          {{ banner.text }}
        </v-alert>
      </v-col>
    </v-row>

    <div v-if="selectedCode">
      <v-progress-linear
        v-if="apiMode && partsLoading"
        indeterminate
        color="red-darken-3"
        class="grafiche-parts-progress"
      />
      <v-table
        density="comfortable"
        class="grafiche-lines-table rounded border"
      >
        <thead>
          <tr>
            <th>
              {{ $t("leko_employees.car_part_groups.grafiche_part_code") }}
            </th>
            <th>IT</th>
            <th>EN</th>
            <th>SQ</th>
            <th v-if="apiMode">
              {{ $t("leko_employees.car_part_groups.grafiche_enabled_column") }}
            </th>
            <th v-if="!apiMode" class="text-right">
              {{ $t("leko_employees.car_part_groups.grafiche_actions") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in currentLines"
            :key="row._id"
            :class="idx % 2 === 0 ? 'bg-grey-lighten-4' : ''"
          >
            <td style="max-width: 140px">
              <v-text-field
                v-if="!apiMode"
                v-model="row.partCode"
                hide-details
                density="compact"
                variant="outlined"
              />
              <span v-else class="text-body-2">{{ row.partCode }}</span>
            </td>
            <td>
              <v-text-field
                v-if="!apiMode"
                v-model="row.title.it"
                hide-details
                density="compact"
                variant="outlined"
              />
              <span v-else class="text-body-2">{{ row.title.it }}</span>
            </td>
            <td>
              <v-text-field
                v-if="!apiMode"
                v-model="row.title.en"
                hide-details
                density="compact"
                variant="outlined"
              />
              <span v-else class="text-body-2">{{ row.title.en }}</span>
            </td>
            <td>
              <v-text-field
                v-if="!apiMode"
                v-model="row.title.sq"
                hide-details
                density="compact"
                variant="outlined"
              />
              <span v-else class="text-body-2">{{ row.title.sq }}</span>
            </td>
            <td v-if="apiMode">
              <v-switch
                :model-value="row.enabled !== false"
                color="red-darken-3"
                density="compact"
                hide-details
                :disabled="row._toggling"
                @update:model-value="(v) => onApiToggleEnabled(row, v)"
              />
            </td>
            <td v-if="!apiMode" class="text-right">
              <v-btn
                icon
                variant="text"
                color="red-darken-2"
                @click="removeLine(row._id)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
              <v-btn
                icon
                variant="text"
                color="grey-darken-2"
                @click="openLineEditor(row)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>

    <v-sheet
      v-else
      class="pa-6 text-medium-emphasis text-center border rounded"
    >
      {{ $t("leko_employees.car_part_groups.grafiche_pick_category_hint") }}
    </v-sheet>

    <v-dialog v-model="lineDialog" max-width="520">
      <v-card>
        <v-card-title class="redColor text-white d-flex">
          <span>{{
            editingLine
              ? $t("leko_employees.car_part_groups.grafiche_edit_title")
              : $t("leko_employees.car_part_groups.grafiche_add_title")
          }}</span>
          <v-spacer />
          <v-btn icon variant="text" color="white" @click="lineDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="lineEditor.partCode"
            :label="$t('leko_employees.car_part_groups.grafiche_part_code')"
            variant="outlined"
            hide-details
            class="mb-3"
          />
          <v-text-field
            v-model="lineEditor.it"
            label="Italiano"
            variant="outlined"
            hide-details
            class="mb-3"
          />
          <v-text-field
            v-model="lineEditor.en"
            label="English"
            variant="outlined"
            hide-details
            class="mb-3"
          />
          <v-text-field
            v-model="lineEditor.sq"
            label="Shqip"
            variant="outlined"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="outlined" @click="lineDialog = false">
            {{ $t("leko_employees.car_part_groups.close") }}
          </v-btn>
          <v-btn color="red-darken-3" variant="flat" @click="applyLineEditor">
            {{ $t("leko_employees.car_part_groups.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  GRAFICHE_CATALOG_UPDATED_EVENT,
  loadGraficheCatalog,
  saveGraficheCatalog,
} from "@/services/graficheCatalogAdminStore.js";
import { putPartEnabled } from "@/api/partCategoryGroups.js";
import {
  fetchPartCategoryGroupsCategoriesOnly,
  fetchPartsTemplatesForCategory,
} from "@/services/partCategoryGroupsCatalog.js";

function newLineId(categoryCode) {
  return `local-${categoryCode}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default {
  name: "GraficheTitlesTab",

  data: () => ({
    apiMode: false,
    reloadLoading: false,
    partsLoading: false,
    apiLinesByCategory: {},
    /** Dedupe concurrent getParts calls per category id */
    apiPartsInflight: {},
    catalog: { categories: [], linesByCategory: {} },
    selectedCode: null,
    banner: null,
    lineDialog: false,
    editingLine: null,
    lineEditor: { partCode: "", it: "", en: "", sq: "" },
  }),

  computed: {
    categorySelectItems() {
      const list = [...(this.catalog.categories || [])].sort(
        (a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0),
      );
      return list.map((c) => ({
        value: c.categoryCode,
        label:
          (c.title && (c.title[this.lang()] || c.title.it)) || c.categoryCode,
      }));
    },

    currentLines() {
      if (!this.selectedCode) return [];
      if (this.apiMode) return this.apiLinesByCategory[this.selectedCode] || [];
      return this.catalog.linesByCategory[this.selectedCode] || [];
    },
  },

  watch: {
    selectedCode(val) {
      if (val && this.apiMode) {
        void this.ensureApiPartsLoaded(val);
      }
    },
  },

  mounted() {
    this.reload();
  },

  methods: {
    lang() {
      return this.$store.getters.language || "it";
    },

    async reload() {
      this.reloadLoading = true;
      this.banner = null;
      const previousSelected = this.selectedCode;
      this.apiPartsInflight = {};
      this.apiLinesByCategory = {};
      this.partsLoading = false;
      try {
        const snap = await fetchPartCategoryGroupsCategoriesOnly();
        if (snap.categories?.length > 0) {
          this.apiMode = true;
          this.catalog = {
            categories: snap.categories.map((c) => ({ ...c })),
            linesByCategory: {},
          };
          const validCodes = new Set(
            snap.categories.map((c) => c.categoryCode),
          );
          this.selectedCode =
            previousSelected && validCodes.has(previousSelected)
              ? previousSelected
              : null;
        } else {
          throw new Error("empty categories");
        }
      } catch {
        this.apiMode = false;
        this.apiLinesByCategory = {};
        this.catalog = loadGraficheCatalog();
        for (const code of Object.keys(this.catalog.linesByCategory || {})) {
          const rows = this.catalog.linesByCategory[code];
          for (const r of rows) {
            if (!r.title) r.title = { it: "", en: "", sq: "" };
            if (r.title.it == null) r.title.it = "";
            if (r.title.en == null) r.title.en = "";
            if (r.title.sq == null) r.title.sq = "";
          }
        }

        const codes = [...(this.catalog.categories || [])].map(
          (c) => c.categoryCode,
        );
        if (!this.selectedCode || !codes.includes(this.selectedCode)) {
          const sorted = [...(this.catalog.categories || [])].sort(
            (a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0),
          );
          this.selectedCode = sorted[0]?.categoryCode ?? null;
        }
      }

      if (this.apiMode && this.selectedCode) {
        await this.ensureApiPartsLoaded(this.selectedCode);
      }

      this.reloadLoading = false;
    },

    async ensureApiPartsLoaded(categoryId) {
      if (!categoryId || !this.apiMode) return;
      if (
        Object.prototype.hasOwnProperty.call(
          this.apiLinesByCategory,
          categoryId,
        )
      ) {
        return;
      }

      const inflight = this.apiPartsInflight[categoryId];
      if (inflight) {
        await inflight;
        return;
      }

      const p = fetchPartsTemplatesForCategory(categoryId)
        .then((rows) => {
          if (this.selectedCode !== categoryId) return;
          const shaped = rows.map((r) => ({
            ...r,
            title: { ...r.title },
          }));
          for (const r of shaped) {
            if (!r.title) r.title = { it: "", en: "", sq: "" };
            if (r.title.it == null) r.title.it = "";
            if (r.title.en == null) r.title.en = "";
            if (r.title.sq == null) r.title.sq = "";
            r.enabled = r.enabled !== false;
          }
          this.apiLinesByCategory = {
            ...this.apiLinesByCategory,
            [categoryId]: shaped,
          };
        })
        .catch(() => {
          if (this.selectedCode === categoryId) {
            this.apiLinesByCategory = {
              ...this.apiLinesByCategory,
              [categoryId]: [],
            };
          }
        })
        .finally(() => {
          delete this.apiPartsInflight[categoryId];
          this.partsLoading = Object.keys(this.apiPartsInflight).length > 0;
        });

      this.apiPartsInflight[categoryId] = p;
      this.partsLoading = true;
      await p;
    },

    async onApiToggleEnabled(row, enabled) {
      if (!this.selectedCode || !this.apiMode) return;
      const partCode = Number(row.partCode);
      if (Number.isNaN(partCode)) {
        this.banner = {
          type: "error",
          text: this.$t(
            "leko_employees.car_part_groups.grafiche_toggle_enabled_error",
          ),
        };
        return;
      }
      row._toggling = true;
      try {
        await putPartEnabled(this.selectedCode, partCode, !!enabled);
        row.enabled = !!enabled;
        window.dispatchEvent(new CustomEvent(GRAFICHE_CATALOG_UPDATED_EVENT));
      } catch {
        this.banner = {
          type: "error",
          text: this.$t(
            "leko_employees.car_part_groups.grafiche_toggle_enabled_error",
          ),
        };
      } finally {
        row._toggling = false;
      }
    },

    persist() {
      if (this.apiMode) return;
      saveGraficheCatalog(this.catalog);
      this.banner = {
        type: "success",
        text: this.$t("leko_employees.car_part_groups.grafiche_lines_saved"),
      };
    },

    ensureTitleShape(row) {
      if (!row.title) row.title = { it: "", en: "", sq: "" };
    },

    openLineEditor(row) {
      if (this.apiMode) return;
      this.editingLine = row;
      if (row) {
        this.ensureTitleShape(row);
        this.lineEditor = {
          partCode: row.partCode || "",
          it: row.title.it ?? "",
          en: row.title.en ?? "",
          sq: row.title.sq ?? "",
        };
      } else {
        this.lineEditor = { partCode: "", it: "", en: "", sq: "" };
      }
      this.lineDialog = true;
    },

    applyLineEditor() {
      if (this.apiMode) return;
      if (!this.selectedCode) return;
      if (!this.catalog.linesByCategory[this.selectedCode]) {
        this.catalog.linesByCategory[this.selectedCode] = [];
      }
      const ref = this.catalog.linesByCategory[this.selectedCode];

      if (!this.editingLine) {
        ref.push({
          _id: newLineId(this.selectedCode),
          partCode:
            this.lineEditor.partCode?.trim() || `LINE_${ref.length + 1}`,
          title: {
            it: this.lineEditor.it ?? "",
            en: this.lineEditor.en ?? "",
            sq: this.lineEditor.sq ?? "",
          },
        });
      } else {
        Object.assign(this.editingLine, {
          partCode:
            this.lineEditor.partCode?.trim() || this.editingLine.partCode,
          title: {
            it: this.lineEditor.it ?? "",
            en: this.lineEditor.en ?? "",
            sq: this.lineEditor.sq ?? "",
          },
        });
      }
      this.lineDialog = false;
      this.persist();
    },

    removeLine(id) {
      if (this.apiMode) return;
      if (!this.selectedCode) return;
      const ref = this.catalog.linesByCategory[this.selectedCode];
      if (!ref) return;
      const i = ref.findIndex((r) => r._id === id);
      if (i > -1) ref.splice(i, 1);
      this.persist();
    },
  },
};
</script>

<style scoped>
.grafiche-lines-table {
  overflow-x: auto;
}
</style>
